import { EmailFormData } from '@/pages/campaign/emails'
import { emailNotificationSchema } from '@/schema/ec-flow-texts.schema'
import { Button, useToast } from '@alphabait/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { Trash } from '@phosphor-icons/react'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

// Reuse existing custom components
import { CustomDateComponent } from '../form/custom-date'
import CustomFileUpload from '../form/custom-file-upload'
import { CustomMultiSelect } from '../form/custom-multiselect'
import { CustomShadcnSelectComponent } from '../form/custom-shadcn-select'
import WYSIWYG from '../form/custom-wysiwyg-editor'
import { FormInput } from '../form-fields'

interface EmailFormProps {
  onDelete?: () => void
  onClose?: () => void
  onSubmit: (data: EmailFormData) => void
  header: string
  triggerType: string
  values?: EmailFormData
  existingTriggerTypes: string[]
  currentIndex: number
  campaignId: string
  campaignStartDate: string
  campaignEndDate: string
  customerId?: string
}

export const EmailForm = ({
  onDelete,
  onClose,
  onSubmit,
  triggerType,
  values,
  existingTriggerTypes,
  currentIndex,
  campaignId,
  campaignStartDate,
  campaignEndDate,
  customerId,
}: EmailFormProps) => {
  const { t } = useTranslation()
  const { toast } = useToast()

  // Initialize form with react-hook-form
  const { control, handleSubmit, watch, setValue, reset } = useForm<EmailFormData>({
    resolver: zodResolver(emailNotificationSchema),
    defaultValues: values ?? {},
  })

  // Reset form when values change (e.g., when opening a different email)
  useEffect(() => {
    reset(values ?? {})
  }, [values, reset])

  // Watch trigger field for conditional logic
  const selectedTrigger = watch('trigger')

  // Email templates (not translated as they're used in emails)
  const emailTemplates = {
    noActionTaken: {
      header: 'Maak uw cadeaukeuze af',
      text: 'We zien dat u uw cadeau nog niet heeft geselecteerd. Mis deze kans niet!',
      imageUrl: '',
    },
    incompleteOnlineProcess: {
      header: 'Uw cadeaukeuze is niet afgerond',
      text: 'U bent er bijna! Rond uw cadeauselectie af om uw beloning te ontvangen.',
      imageUrl: '',
    },
    happinessCheck: {
      header: 'Deel uw positieve ervaring',
      text: 'Bedankt voor uw positieve feedback! We zouden het geweldig vinden als u uw ervaring wilt delen.',
      imageUrl: '',
      happinessRating: [],
    },
  }

  type EmailTriggerType = keyof typeof emailTemplates

  // Handle trigger change to auto-fill template (only for new emails, not when editing)
  useEffect(() => {
    // Only auto-fill if this is a new email (no existing values) or if the trigger type changed
    const isNewEmail = !values || !values.header
    const triggerChanged = selectedTrigger !== triggerType

    if (selectedTrigger && selectedTrigger in emailTemplates && (isNewEmail || triggerChanged)) {
      const template = emailTemplates[selectedTrigger as EmailTriggerType]
      setValue('header', template.header)
      setValue('text', template.text)
      if (!watch('imageUrl')) {
        setValue('imageUrl', template.imageUrl)
      }
    }
  }, [selectedTrigger, setValue, values, triggerType])

  const onFormSubmit = (data: EmailFormData) => {
    const triggerExists = existingTriggerTypes.some(
      (type, index) => type === data.trigger && index !== currentIndex,
    )

    if (triggerExists) {
      toast({
        title: t('emails.toast'),
        description: t('emails.toast_description'),
        duration: 3000,
      })
      return
    }

    onSubmit(data)
    if (onClose) {
      onClose()
    }
  }

  // Trigger options
  const triggerOptions = [
    {
      value: 'successfullySelectedIncentive',
      label: t('Successfully Selecting an Incentive'),
      disabled:
        existingTriggerTypes.includes('successfullySelectedIncentive') &&
        triggerType !== 'successfullySelectedIncentive',
    },
    {
      value: 'noActionTaken',
      label: t('emails.no_action_taken'),
      disabled:
        existingTriggerTypes.includes('noActionTaken') &&
        triggerType !== 'noActionTaken',
    },
    {
      value: 'incompleteOnlineProcess',
      label: t('emails.incomplete_online_process'),
      disabled:
        existingTriggerTypes.includes('incompleteOnlineProcess') &&
        triggerType !== 'incompleteOnlineProcess',
    },
    {
      value: 'happinessCheck',
      label: t('emails.happiness_check'),
      disabled:
        existingTriggerTypes.includes('happinessCheck') &&
        triggerType !== 'happinessCheck',
    },
  ]

  // Happiness rating options
  const happinessRatingOptions = [
    { value: '4', label: t('emails.rating_4') },
    { value: '5', label: t('emails.rating_5') },
  ]

  return (
    <form onSubmit={handleSubmit(onFormSubmit)}>
      {/* Image Upload - visible for all except successfullySelectedIncentive */}
      {selectedTrigger !== 'successfullySelectedIncentive' && (
        <div className="mb-4" data-testid="email-form-image-upload">
          <label className="block mb-2">{t('upload_image')}</label>
          <CustomFileUpload
            name="imageUrl"
            control={control}
            uploadUrl="/upload/email-notification"
            uploadBody={{
              campaignId,
              triggerType,
            }}
            allowedFileTypes={['jpg', 'png']}
            testId="email-image-upload"
          />
        </div>
      )}

      {/* Trigger Select */}
      <div className="mb-4" data-testid="email-form-trigger">
        <label className="block mb-2">{t('emails.trigger')}</label>
        <CustomShadcnSelectComponent
          name="trigger"
          control={control}
          options={triggerOptions}
          placeholder={t('emails.trigger_description')}
          disabled={triggerType === 'successfullySelectedIncentive'}
        />
      </div>

      {/* 24h notice for certain triggers */}
      {(selectedTrigger === 'incompleteOnlineProcess' ||
        selectedTrigger === 'happinessCheck') && (
          <p
            className="text-blue-700 text-sm mb-2"
            data-testid="email-24h-notice"
          >
            {t('emails.email_sent_24h_after')}
          </p>
        )}

      {/* Date picker - visible only for noActionTaken */}
      {selectedTrigger === 'noActionTaken' && (
        <div className="mb-4" data-testid="email-form-date">
          <label className="block mb-2">{t('emails.send_out')}</label>
          <CustomDateComponent
            name="date"
            control={control}
            minDate={new Date(campaignStartDate)}
            maxDate={new Date(campaignEndDate)}
            testId="email-send-date"
          />
        </div>
      )}

      {/* Happiness Rating - visible only for happinessCheck */}
      {selectedTrigger === 'happinessCheck' && (
        <div className="mb-4" data-testid="email-form-happiness-rating">
          <label className="block mb-2">{t('emails.rating')}</label>
          <CustomMultiSelect
            field={{
              name: 'happinessRating',
              type: 'custom',
              customControlType: 'multi-select',
              options: happinessRatingOptions,
              uiOptions: { placeholder: t('emails.select_rating') },
            }}
            formProps={{ control } as any}
          />
        </div>
      )}

      {/* Email Header */}
      <FormInput
        name="header"
        control={control}
        label={t('emails.email_heading')}
        labelClassName="block mb-2"
        type="text"
        inputClassName="w-full border border-gray-300 rounded px-3 py-2"
        wrapperClassName="mb-4"
        testId="email-header-input"
      />

      {/* Email Text - WYSIWYG Editor */}
      <div className="mb-4" data-testid="email-form-text">
        <label className="block mb-2">{t('emails.email_text')}</label>
        <WYSIWYG
          name="text"
          control={control}
          className="h-40"
          maxChars={500}
          customerId={customerId}
          disableFontOptions
        />
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between pt-8">
        <Button
          type="button"
          variant="outline"
          onClick={onDelete}
          disabled={
            triggerType === 'successfullySelectedIncentive' || !values?.trigger
          }
          data-testid="email-delete-button"
        >
          <Trash className="mr-2 h-4 w-4 text-primary" />
          {t('emails.delete_email')}
        </Button>
        <div className="grid grid-cols-2 gap-4 justify-end items-end">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="border border-primary text-primary hover:border-primary/80 hover:text-primary/80"
            data-testid="email-cancel-button"
          >
            {t('cancel')}
          </Button>
          <Button
            className="!text-white cursor-pointer"
            type="submit"
            variant="default"
            data-testid="email-save-button"
          >
            {t('save')}
          </Button>
        </div>
      </div>
    </form>
  )
}